import * as React from "react";
import {Component } from "react";

import {observer} from "mobx-react";
import {AppState} from "../../components/app/app-mobx";

import {Button, Divider, Header, Item, Popup, Table} from "semantic-ui-react";
import {MapViewerConfiguration} from "../../config/map-viewer-configuration";
import {NavigationConfiguration} from "../../config/navigation-configuration";
import {AppConfiguration} from "../../core/config/app-configuration";

import {MapState} from "../../core/map-state";
import {Auftragsverwaltung} from "../../stores/auftragsverwaltung-mobx";
import {Contractor} from "../../models/contractor";
import {ContractorForm} from "./contractor-form";
import {Icon} from "../../core/components/icon/icon";
import {ProjectList} from "../projects/project-list";
import {EditorTrackingMetadata} from "../../components/app/metadata/editor-tracking-metadata";
import {PathUtils} from "../../utils/path-utils";

export interface ContractorDetailsProps {
    view: __esri.MapView | __esri.SceneView | undefined;
    auftragsverwaltung: Auftragsverwaltung;
    appStore: AppState;
    mapStore: MapState;
    mapViewerConfig: MapViewerConfiguration;
    appConfig: AppConfiguration;
    handleErrorAndNotFound: () => void;
}

@observer
export class ContractorDetails extends Component<ContractorDetailsProps, any> {
    constructor(props: ContractorDetailsProps) {
        super(props);
        this.state = {
            projectsLoaded: false,
            highlightedItemId: null,
            graphic: null,
            statusCodes: null
        };
    }

    componentDidMount(): void {
        const {
            appStore,
            auftragsverwaltung,
        } = this.props;

        const contractor = auftragsverwaltung.selectedContractor;

        if (!contractor?.id) {
            this.props.handleErrorAndNotFound();
            return;
        }

        this.loadDomains();
        this.loadProjects(contractor);

        auftragsverwaltung.setShowMap(false);

        const {topic} = appStore;

        let newRoute = "";
        if (contractor.isNewRecord) {
            newRoute = `${topic.routes[0]}/neu`;
        } else {
            newRoute = `${topic.routes[0]}/${contractor.id}`;
        }
        const breadcrumbs = new NavigationConfiguration();
        breadcrumbs.items = [
            { href: topic.routes[0], title: topic.label },
            { href: newRoute, title: contractor.isNewRecord ? "Neuer Auftragnehmer" : contractor.organisation }
        ];
        appStore.setBreadcrumbs(breadcrumbs);
        appStore.setRoutePath(newRoute);

        const pageTitle = contractor.isNewRecord ?
            `${topic.label} - Neuer Auftragnehmer` :
            `${contractor.organisation}`;

        appStore.setPageTitle(pageTitle);
        appStore.setTopContent(null);
        appStore.setBottomContent(null);
    }

    componentWillUnmount(): void {
    }

    handleToggleEditing(e: React.MouseEvent): void {
        e.preventDefault();
        const target = e.target as HTMLElement;
        target.blur();

        const {auftragsverwaltung} = this.props;
        const {isEditing, editingObjectType} = auftragsverwaltung;

        if (isEditing) {
            if (editingObjectType === "contractor") {
                // do only turn of editing for this object type
                auftragsverwaltung.setEditing(false);
                auftragsverwaltung.resetStatus();

                const contractor = auftragsverwaltung.selectedContractor;
                if (contractor?.isNewRecord) {
                    this.cancelNewContractor(contractor);
                }
            }
        } else {
            // do not show map when editing
            auftragsverwaltung.setShowMap(false);
            auftragsverwaltung.setEditing(true, "contractor");
        }
    }

    handleDelete(e: React.MouseEvent, contractor: Contractor): void {
        e.preventDefault();
        const target = e.target as HTMLElement;
        target.blur();

        if (confirm("Auftragnehmer wirklich löschen?")) {
            const {appStore, auftragsverwaltung} = this.props;

            const user = appStore.esriCredential?.userId || "";
            auftragsverwaltung.ContractorStore.delete(contractor, user).then(() => {
                this.goBackToList();
            });
        }
    }

    cancelNewContractor(contractor: Contractor): void {
        if (!contractor) {
            return;
        }

        const {auftragsverwaltung} = this.props;

        auftragsverwaltung.ContractorStore.removeItem(contractor);
        auftragsverwaltung.ContractorStore.resetSelectedItem();
        this.goBackToList();
    }

    goBackToList(): void {
        const {appStore} = this.props;

        appStore.clearObjectId();
        // go back to base route (project list)
        appStore.setRoutePath(appStore.topic?.routes[0]);
    }

    render(): React.ReactElement | null {
        const {
            appStore,
            auftragsverwaltung,
            mapViewerConfig,
            view
        } = this.props;

        const {projectsLoaded, statusCodes} = this.state;

        const contractor = auftragsverwaltung.selectedContractor;
        if (!contractor?.id) {
            return null;
        }

        let element: React.ReactElement | null = null;
        if (auftragsverwaltung.isEditingContractor) {
            element = (
                <Item.Content>
                    <Item.Description>
                        <ContractorForm
                            view={view}
                            appStore={appStore}
                            auftragsverwaltung={auftragsverwaltung}
                            contractor={contractor}
                            onCancel={(e: React.MouseEvent) => {
                                this.handleToggleEditing(e);
                            }}
                        />
                    </Item.Description>
                </Item.Content>
            );
        }
        else {
            element = (
                <Item.Content>
                    <Item.Description>
                        <Table basic="very" compact>
                            <Table.Body>
                                <Table.Row>
                                    <Table.Cell collapsing singleLine>ID</Table.Cell>
                                    <Table.Cell>{contractor.id}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell singleLine>Organisation</Table.Cell>
                                    <Table.Cell>{contractor.organisation}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell singleLine>Kontakt</Table.Cell>
                                    <Table.Cell>{contractor.contact}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell singleLine>Email</Table.Cell>
                                    <Table.Cell>{contractor.email}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell singleLine>Strasse</Table.Cell>
                                    <Table.Cell>{`${contractor.street} ${contractor.streetNumber}`}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell singleLine>Postleitzahl / Ort</Table.Cell>
                                    <Table.Cell>{`${contractor.postalCode} ${contractor.city}`}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell singleLine>Postfach</Table.Cell>
                                    <Table.Cell>{contractor.postbox}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell singleLine>Bemerkungen</Table.Cell>
                                    <Table.Cell>{contractor.notes}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell singleLine>Status</Table.Cell>
                                    <Table.Cell>{statusCodes?.get(contractor.statusId)}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell singleLine>Datenportal</Table.Cell>
                                    <Table.Cell>
                                        <a href={PathUtils.joinPath(mapViewerConfig.dataPortal.homeUrl, contractor.id?.toString())}
                                            className="link" target="_blank" rel="noreferrer">
                                            <span className="linked">Konto anzeigen</span>
                                        </a>
                                    </Table.Cell>
                                </Table.Row>
                            </Table.Body>
                        </Table></Item.Description>
                </Item.Content>
            );
        }

        return (
            <div>
                {!auftragsverwaltung.isEditingContractor &&
                    contractor.isAlterable &&
                    <Popup content="Auftragnehmer ändern"
                        trigger={
                            <Button className={"editing"} circular
                                onClick={(e: React.MouseEvent) => {
                                    this.handleToggleEditing(e);
                                }}>
                                <Icon name={"pencil"} selected/>
                            </Button>
                        } />
                }
                {!auftragsverwaltung.isEditingContractor && !contractor.isNewRecord && contractor.isDeletable &&
                    <Popup content="Auftragnehmer löschen"
                        trigger={
                            <Button className={"delete"} circular data={{}}
                                onClick={(e: React.MouseEvent) => {
                                    this.handleDelete(e, contractor);
                                }}>
                                <Icon name={"garbage_can"} selected/>
                            </Button>
                        } />
                }
                <Item.Group>
                    <Item>{element}</Item>
                </Item.Group>
                {projectsLoaded && !auftragsverwaltung.isEditingContractor && !contractor.isNewRecord &&
                    contractor.hasProjects &&
                    <>
                        <Divider horizontal>
                            <Header as="h2">
                                <Icon name="registry" />
                                Projekte
                            </Header>
                        </Divider>
                        <ProjectList
                            appStore={appStore}
                            auftragsverwaltung={auftragsverwaltung}
                            mapViewerConfig={mapViewerConfig}
                            projects={contractor.projects}
                            listOnly={true}
                        />
                    </>
                }
                <EditorTrackingMetadata editableItem={contractor} />
            </div>
        );
    }

    /**
     * Load domains and sources for select boxes.
     *
     * @private
     */
    private loadDomains() {
        const {auftragsverwaltung} = this.props;

        auftragsverwaltung.getShortDomainCodes("contractor", "statusId").then((statusCodes) => {
            this.setState({
                statusCodes
            });
        });
    }

    private loadProjects(contractor: Contractor): void {
        const {auftragsverwaltung} = this.props;

        if (!contractor?.id) {
            return;
        }

        auftragsverwaltung.ProjectStore.loadItems(undefined, [contractor.id]).then((projects) => {
            contractor.projectsLoaded = true;
            contractor.projects = projects;
            this.setState({projectsLoaded: true});
        });
    }
}
